import { CAMPOS_DA_LISTA, lerMes, type CampoDaLista, type FormatoDaPlanilha } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { campo, rotulo } from "../estilos";
import { celulaEmTexto, type Planilha } from "./ler-planilha";

export type ConfigDasColunas = {
  formato: FormatoDaPlanilha;
  cabecalho: number;
  colunas: Partial<Record<CampoDaLista, number>>;
  colunaDoRotulo: number;
  colunasDeMes: number[];
  jaPagos: boolean;
};

const OBRIGATORIOS: CampoDaLista[] = ["data", "valor"];

/** Passo 2: que coluna é o quê (lista) ou onde estão as contas e os meses (DRE mensal). */
export function PassoColunas({ planilha, config, onChange }: { planilha: Planilha; config: ConfigDasColunas; onChange: (c: ConfigDasColunas) => void }) {
  const cabecalho = planilha.linhas[config.cabecalho] ?? [];
  const amostra = planilha.linhas[config.cabecalho + 1] ?? [];
  const nomes = cabecalho.map((c, i) => celulaEmTexto(c) || `Coluna ${i + 1}`);
  const set = (p: Partial<ConfigDasColunas>) => onChange({ ...config, ...p });

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <label className="w-56">
          <span className={rotulo}>Formato da planilha</span>
          <select value={config.formato} onChange={(e) => set({ formato: e.target.value as FormatoDaPlanilha })} className={campo}>
            <option value="LISTA">Lista de lançamentos</option>
            <option value="DRE_MENSAL">DRE por mês (contas × meses)</option>
          </select>
        </label>
        <label className="w-40">
          <span className={rotulo}>Linha do cabeçalho</span>
          <input type="number" min={1} max={planilha.linhas.length} value={config.cabecalho + 1} onChange={(e) => set({ cabecalho: Math.max(0, Number(e.target.value) - 1) })} className={campo} />
        </label>
        <label className="flex items-center gap-2 pb-2 text-sm text-zinc-700 dark:text-foreground">
          <input type="checkbox" checked={config.jaPagos} onChange={(e) => set({ jaPagos: e.target.checked })} className="h-4 w-4 accent-brand-600" />
          {config.formato === "LISTA" ? "Já pagos e recebidos (sem coluna de pagamento, vale a data)" : "Já pagos no próprio mês"}
        </label>
      </div>

      {config.formato === "LISTA" ? (
        <div className="overflow-x-auto rounded-lg border border-zinc-200 dark:border-border">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-zinc-50 text-left text-[11px] font-semibold uppercase tracking-wide text-zinc-500 dark:bg-muted/40 dark:text-muted-foreground">
                <th className="px-3 py-2">Coluna da planilha</th>
                <th className="px-3 py-2">Exemplo</th>
                <th className="px-3 py-2">Campo</th>
              </tr>
            </thead>
            <tbody>
              {nomes.map((n, i) => {
                const atual = (Object.entries(config.colunas).find(([, v]) => v === i)?.[0] ?? "") as CampoDaLista | "";
                return (
                  <tr key={i} className="border-t border-zinc-100 dark:border-border/50">
                    <td className="px-3 py-1.5 font-medium text-zinc-900 dark:text-foreground">{n}</td>
                    <td className="max-w-[200px] truncate px-3 py-1.5 text-zinc-500">{celulaEmTexto(amostra[i])}</td>
                    <td className="px-3 py-1.5">
                      <select
                        value={atual}
                        onChange={(e) => {
                          const colunas = Object.fromEntries(Object.entries(config.colunas).filter(([k, v]) => v !== i && k !== e.target.value));
                          if (e.target.value) colunas[e.target.value] = i;
                          set({ colunas });
                        }}
                        className={cn(campo, "h-8")}
                      >
                        <option value="">Ignorar</option>
                        {CAMPOS_DA_LISTA.map((c) => (
                          <option key={c.key} value={c.key}>
                            {c.label}
                            {OBRIGATORIOS.includes(c.key) ? " (obrigatório)" : ""}
                          </option>
                        ))}
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="space-y-3">
          <label className="block sm:w-72">
            <span className={rotulo}>Coluna das contas (Aluguel, Salários...)</span>
            <select value={config.colunaDoRotulo} onChange={(e) => set({ colunaDoRotulo: Number(e.target.value) })} className={campo}>
              {nomes.map((n, i) => (
                <option key={i} value={i}>
                  {n}
                </option>
              ))}
            </select>
          </label>
          <div>
            <span className={rotulo}>Meses a importar</span>
            <div className="flex flex-wrap gap-1.5">
              {cabecalho.map((c, i) => {
                const mes = lerMes(c);
                if (!mes) return null;
                const marcado = config.colunasDeMes.includes(i);
                return (
                  <button
                    key={i}
                    type="button"
                    aria-pressed={marcado}
                    onClick={() => set({ colunasDeMes: marcado ? config.colunasDeMes.filter((x) => x !== i) : [...config.colunasDeMes, i] })}
                    className={cn("rounded-full border px-2.5 py-1 text-xs", marcado ? "border-brand-600 bg-brand-50 text-brand-700 dark:border-primary dark:bg-primary/10 dark:text-primary" : "border-zinc-200 text-zinc-500 dark:border-border")}
                  >
                    {mes.slice(5, 7)}/{mes.slice(0, 4)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const colunasProntas = (c: ConfigDasColunas) => (c.formato === "LISTA" ? OBRIGATORIOS.every((k) => c.colunas[k] != null) : c.colunasDeMes.length > 0);
